"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.JOB_IMAGE_EVENTS = exports.makeJobImageSchema = exports.previewJobPostPromptSchema = exports.POST_MIN_CONTRAST = exports.saveJobPostDesignSchema = exports.ventureDesignDefaultSchema = exports.DEFAULT_POST_DESIGN = exports.jobPostDesignSchema = exports.postReferenceSchema = exports.POST_INSTRUCTIONS_MAX = exports.POST_REFERENCE_MAX_BYTES = exports.MAX_POST_REFERENCES = exports.POST_REFERENCE_ROLE_LABELS = exports.POST_REFERENCE_ROLES = exports.POST_PERSON_DRESS_LABELS = exports.POST_PERSON_AGE_LABELS = exports.POST_PERSON_GENDER_LABELS = exports.POST_PERSON_DRESS = exports.POST_PERSON_AGES = exports.POST_PERSON_GENDERS = exports.POST_PICTURE_KIND_LABELS = exports.POST_PICTURE_KINDS = exports.POST_FONT_STYLE_LABELS = exports.POST_FONT_STYLES = exports.POST_PALETTE_PRESETS = exports.POST_SWATCHES = exports.POST_BLACK = exports.POST_WHITE = exports.BRAND_POST_PALETTE = exports.postPaletteSchema = exports.POST_COLOR_ROLE_USES = exports.POST_COLOR_ROLE_LABELS = exports.POST_COLOR_ROLES = exports.saveJobPostWordsSchema = exports.jobPostWordsSchema = exports.POST_WORD_LIMITS = exports.MAX_POST_POINTS = exports.JOB_IMAGE_RUN_STATUSES = void 0;
exports.contrastRatio = contrastRatio;
exports.postContrastProblems = postContrastProblems;
exports.samePostDesign = samePostDesign;
exports.samePostWords = samePostWords;
const zod_1 = require("zod");
// ADR 0064, amended by ADR 0065 — the advert image Nano Banana 2 makes for a job (the `job_image` module). The words
// on the post are prepared from the job for free; a person checks or edits them, then approves, and only then is an
// image made and paid for (owner, 2026-10-28).
exports.JOB_IMAGE_RUN_STATUSES = ['making', 'done', 'failed'];
/** The most points a post carries, and the longest each may be (03-content-rules §2: never cut, never "…"). */
exports.MAX_POST_POINTS = 3;
/** How long each line on the post may be, so it never wraps badly or gets cut. */
exports.POST_WORD_LIMITS = {
    headline: 24,
    role: 64,
    facts: 48,
    point: 40,
    button: 16,
    applyBy: 32,
    applyAt: 40,
};
function line(max, label) {
    return zod_1.z
        .string()
        .trim()
        .max(max, `${label} can be up to ${max} characters, so it reads on a phone.`);
}
/** The words on the post, in reading order. An empty line is left off the image. */
exports.jobPostWordsSchema = zod_1.z.object({
    headline: line(exports.POST_WORD_LIMITS.headline, 'The headline').min(1, 'Write a headline.'),
    role: line(exports.POST_WORD_LIMITS.role, 'The role').min(1, 'Write the role.'),
    facts: line(exports.POST_WORD_LIMITS.facts, 'The facts line'),
    points: zod_1.z.array(line(exports.POST_WORD_LIMITS.point, 'A point')).max(exports.MAX_POST_POINTS),
    button: line(exports.POST_WORD_LIMITS.button, 'The button'),
    applyBy: line(exports.POST_WORD_LIMITS.applyBy, 'The apply-by line'),
    applyAt: line(exports.POST_WORD_LIMITS.applyAt, 'The apply-at line'),
});
exports.saveJobPostWordsSchema = zod_1.z.object({ words: exports.jobPostWordsSchema });
// --- The design: colours, fonts, picture, own instructions and own images (ADR 0066) -----------------------------
/** The five colour roles of the toolbar (owner's reference, 2026-10-28), in its order. */
exports.POST_COLOR_ROLES = ['text', 'background', 'primary', 'secondary', 'accent'];
exports.POST_COLOR_ROLE_LABELS = {
    text: 'Text',
    background: 'Background',
    primary: 'Primary',
    secondary: 'Secondary',
    accent: 'Accent',
};
/** What each role colours on the post, for the chip's tooltip and the brief. */
exports.POST_COLOR_ROLE_USES = {
    text: 'the headline and the main words',
    background: 'the whole background',
    primary: 'the role pill and the Apply button',
    secondary: 'soft shapes and secondary words',
    accent: 'the big shape behind the picture and the headline underline',
};
const HEX = /^#[0-9A-F]{6}$/;
const hex = zod_1.z
    .string()
    .trim()
    .transform((value) => value.toUpperCase())
    .pipe(zod_1.z.string().regex(HEX, 'Use a colour code like #53BECB.'));
exports.postPaletteSchema = zod_1.z.object({ text: hex, background: hex, primary: hex, secondary: hex, accent: hex });
/** The brand palette (Design System/project/tokens/colors.css): black, white, grey and a teal accent. */
exports.BRAND_POST_PALETTE = {
    text: '#000000',
    background: '#F1EFEC',
    primary: '#000000',
    secondary: '#8B857D',
    accent: '#53BECB',
};
/** Pure white and black, for words on a filled shape. */
exports.POST_WHITE = '#FFFFFF';
exports.POST_BLACK = '#000000';
/** The colours a chip offers before a code is typed: the brand's, then calm professional ones. No neon. */
exports.POST_SWATCHES = [
    '#000000', '#1C1B19', '#3F3B36', '#5C5751', '#8B857D', '#C9C5BE', '#E0DDD8', '#F1EFEC', '#F6F5F4', '#FFFFFF',
    '#53BECB', '#1C9A86', '#298FB8', '#2189B0', '#0C3651', '#002054', '#1C3563', '#43B7C9', '#2F6B4F', '#6B8F71',
    '#B5452F', '#C9803A', '#D9B44A', '#7A3E65', '#4B3F72', '#9C2F3E', '#E9D8C4', '#DCE8E4', '#D6E4F0', '#EFE3E8',
];
/** Ready-made palettes the toolbar can start from; the brand's first. */
exports.POST_PALETTE_PRESETS = [
    { name: 'Magpie Nest', palette: exports.BRAND_POST_PALETTE },
    { name: 'Ink and paper', palette: { text: '#000000', background: '#FFFFFF', primary: '#000000', secondary: '#C9C5BE', accent: '#E0DDD8' } },
    { name: 'Deep navy', palette: { text: '#FFFFFF', background: '#0C3651', primary: '#FFFFFF', secondary: '#298FB8', accent: '#53BECB' } },
    { name: 'Forest', palette: { text: '#1C1B19', background: '#F6F5F4', primary: '#2F6B4F', secondary: '#6B8F71', accent: '#DCE8E4' } },
    { name: 'Warm clay', palette: { text: '#1C1B19', background: '#F6F5F4', primary: '#B5452F', secondary: '#C9803A', accent: '#E9D8C4' } },
];
/** About six font looks. An image model copies a look, not an exact font (research, 2026-10-28). */
exports.POST_FONT_STYLES = ['bold_condensed', 'clean_modern', 'rounded_friendly', 'classic_serif', 'elegant_thin', 'handwritten'];
exports.POST_FONT_STYLE_LABELS = {
    bold_condensed: 'Bold condensed',
    clean_modern: 'Clean modern',
    rounded_friendly: 'Rounded friendly',
    classic_serif: 'Classic serif',
    elegant_thin: 'Elegant thin',
    handwritten: 'Handwritten accent',
};
exports.POST_PICTURE_KINDS = ['auto', 'person', 'objects', 'none', 'own_photo'];
exports.POST_PICTURE_KIND_LABELS = {
    auto: 'Auto',
    person: 'Person',
    objects: 'Objects',
    none: 'No picture',
    own_photo: 'My photo',
};
exports.POST_PERSON_GENDERS = ['any', 'man', 'woman', 'group'];
exports.POST_PERSON_AGES = ['any', 'young', 'mid', 'senior'];
exports.POST_PERSON_DRESS = ['any', 'formal', 'smart_casual', 'uniform'];
exports.POST_PERSON_GENDER_LABELS = { any: 'Any', man: 'Man', woman: 'Woman', group: 'Small group' };
exports.POST_PERSON_AGE_LABELS = { any: 'Any age', young: 'Young (20s)', mid: 'Mid-career (30s–40s)', senior: 'Senior (50s+)' };
exports.POST_PERSON_DRESS_LABELS = { any: 'Any', formal: 'Formal', smart_casual: 'Smart casual', uniform: 'Work uniform' };
/** What an uploaded picture is for: put it in the post, or copy its style. */
exports.POST_REFERENCE_ROLES = ['include', 'style'];
exports.POST_REFERENCE_ROLE_LABELS = {
    include: 'Put this in the post',
    style: 'Copy this style',
};
/** At most this many own pictures: with the logo that is 7 of the model's 14 (Google's docs). */
exports.MAX_POST_REFERENCES = 6;
exports.POST_REFERENCE_MAX_BYTES = 5 * 1024 * 1024;
exports.POST_INSTRUCTIONS_MAX = 600;
exports.postReferenceSchema = zod_1.z.object({
    documentId: zod_1.z.string().uuid(),
    fileName: zod_1.z.string().trim().min(1).max(200),
    role: zod_1.z.enum(exports.POST_REFERENCE_ROLES),
});
exports.jobPostDesignSchema = zod_1.z.object({
    palette: exports.postPaletteSchema,
    headlineFont: zod_1.z.enum(exports.POST_FONT_STYLES),
    bodyFont: zod_1.z.enum(exports.POST_FONT_STYLES),
    picture: zod_1.z.object({
        kind: zod_1.z.enum(exports.POST_PICTURE_KINDS),
        gender: zod_1.z.enum(exports.POST_PERSON_GENDERS),
        age: zod_1.z.enum(exports.POST_PERSON_AGES),
        dress: zod_1.z.enum(exports.POST_PERSON_DRESS),
    }),
    instructions: zod_1.z.string().trim().max(exports.POST_INSTRUCTIONS_MAX, `Your instructions can be up to ${exports.POST_INSTRUCTIONS_MAX} characters.`),
    references: zod_1.z.array(exports.postReferenceSchema).max(exports.MAX_POST_REFERENCES),
});
exports.DEFAULT_POST_DESIGN = {
    palette: exports.BRAND_POST_PALETTE,
    headlineFont: 'bold_condensed',
    bodyFont: 'clean_modern',
    picture: { kind: 'auto', gender: 'any', age: 'any', dress: 'any' },
    instructions: '',
    references: [],
};
/** The palette and fonts a venture saved as its default (the brand kit's first piece, ADR 0066). */
exports.ventureDesignDefaultSchema = exports.jobPostDesignSchema.pick({ palette: true, headlineFont: true, bodyFont: true });
exports.saveJobPostDesignSchema = zod_1.z.object({ design: exports.jobPostDesignSchema });
/** WCAG relative luminance contrast of two #RRGGBB colours (1 to 21). */
function contrastRatio(a, b) {
    const luminance = (colour) => {
        const [r = 0, g = 0, bl = 0] = [1, 3, 5]
            .map((start) => Number.parseInt(colour.slice(start, start + 2), 16) / 255)
            .map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
        return 0.2126 * r + 0.7152 * g + 0.0722 * bl;
    };
    const first = luminance(a);
    const second = luminance(b);
    return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}
/** Below this, words of that colour are hard to read on that background (WCAG AA for large text). */
exports.POST_MIN_CONTRAST = 3;
/** The pairs on the post that carry words; a chip warns when one is too faint. */
function postContrastProblems(palette) {
    const problems = [];
    if (contrastRatio(palette.text, palette.background) < exports.POST_MIN_CONTRAST)
        problems.push({ role: 'text', message: 'The text is too faint on this background.' });
    if (contrastRatio(palette.primary, palette.background) < exports.POST_MIN_CONTRAST)
        problems.push({ role: 'primary', message: 'The pill and button blend into the background.' });
    return problems;
}
function samePostDesign(a, b) {
    return JSON.stringify(a) === JSON.stringify(b);
}
exports.previewJobPostPromptSchema = zod_1.z.object({
    words: exports.jobPostWordsSchema,
    design: exports.jobPostDesignSchema,
    mode: zod_1.z.enum(['new', 'edit']),
});
/** Approve & make image (`new`), or Update image (`edit`: change only the words that differ on the current image). */
exports.makeJobImageSchema = zod_1.z.object({
    words: exports.jobPostWordsSchema,
    design: exports.jobPostDesignSchema,
    mode: zod_1.z.enum(['new', 'edit']),
});
exports.JOB_IMAGE_EVENTS = {
    REQUESTED: 'job.image_requested',
    MADE: 'job.image_made',
    FAILED: 'job.image_failed',
    RESTORED: 'job.image_restored',
    WORDS_SAVED: 'job.post_words_saved',
    WORDS_RESET: 'job.post_words_reset',
    DESIGN_SAVED: 'job.post_design_saved',
    DESIGN_RESET: 'job.post_design_reset',
    REFERENCE_ADDED: 'job.post_reference_added',
    REFERENCE_REMOVED: 'job.post_reference_removed',
    VENTURE_DEFAULT_SAVED: 'venture.post_design_default_saved',
};
/** Same words, line for line: no need to pay for an update. */
function samePostWords(a, b) {
    return (a.headline === b.headline &&
        a.role === b.role &&
        a.facts === b.facts &&
        a.button === b.button &&
        a.applyBy === b.applyBy &&
        a.applyAt === b.applyAt &&
        a.points.length === b.points.length &&
        a.points.every((point, index) => point === b.points[index]));
}
//# sourceMappingURL=job-image.js.map